/**
 * Feedback Generator — converts rule violations into LLM prompt feedback.
 *
 * Used by the generation pipeline to:
 * 1. Validate generated blocks immediately
 * 2. Create prompt additions for retry generation
 * 3. Track common violations for prompt hardening
 */

import type { RuleViolation, BlockData, PromptFeedback } from "./types"
import { checkAllRules } from "./features"

/** Validate block data and generate prompt feedback */
export function generateFeedback(data: BlockData): PromptFeedback {
  const violations = checkAllRules(data)
  return violationsToFeedback(violations)
}

/** Convert violations to prompt feedback */
export function violationsToFeedback(violations: RuleViolation[]): PromptFeedback {
  const errors = violations
    .filter((v) => v.level === "error")
    .map((v) => `[${v.rule}] ${v.message}`)
  const warnings = violations
    .filter((v) => v.level === "warning")
    .map((v) => `[${v.rule}] ${v.message}`)

  const promptAdditions: string[] = []

  // Generate specific prompt additions based on violation patterns
  const ruleSet = new Set(violations.map((v) => v.rule))

  if (ruleSet.has("interactive-no-binding")) {
    promptAdditions.push(
      "CRITICAL: Every button element MUST have a corresponding event binding in events.json. " +
      'Format: {"elementId:tap": {"flow": "flowName"}}. ' +
      "The flow must exist in flow.json with at least one step.",
    )
  }

  if (ruleSet.has("cart-missing-state")) {
    promptAdditions.push(
      "Cart blocks MUST include setState flows for cart.items (append for add, removeWhere for remove). " +
      "Use appData.cart.items as the state path.",
    )
  }

  if (ruleSet.has("cart-no-iterate")) {
    promptAdditions.push(
      "Cart UI MUST use iterateOver on a grid container to display cart items: " +
      '{"iterateOver": "#{appData.cart.items}", "itemVar": "item"}',
    )
  }

  if (ruleSet.has("auth-no-api")) {
    promptAdditions.push(
      "Auth flows MUST include a restApi step for server-side validation. " +
      "Use POST to /api/auth/login or /api/auth/register.",
    )
  }

  if (ruleSet.has("auth-no-password")) {
    promptAdditions.push(
      'Login forms MUST include a password input field with inputType: "password".',
    )
  }

  if (ruleSet.has("state-write-no-read")) {
    promptAdditions.push(
      "Every setState target should be referenced by at least one UI expression (#{...}). " +
      "Remove unused state writes or add UI elements that display the state.",
    )
  }

  return { errors, warnings, promptAdditions }
}

/** Format feedback as a string for injection into LLM prompts */
export function formatFeedbackForPrompt(feedback: PromptFeedback): string {
  if (feedback.errors.length === 0 && feedback.warnings.length === 0) {
    return ""
  }

  const parts: string[] = []

  if (feedback.errors.length > 0) {
    parts.push("## VALIDATION ERRORS (must fix)")
    parts.push(...feedback.errors.map((e) => `- ${e}`))
  }

  if (feedback.warnings.length > 0) {
    parts.push("## VALIDATION WARNINGS")
    parts.push(...feedback.warnings.map((w) => `- ${w}`))
  }

  if (feedback.promptAdditions.length > 0) {
    parts.push("## REQUIREMENTS")
    parts.push(...feedback.promptAdditions.map((a) => `- ${a}`))
  }

  return parts.join("\n")
}

/** Get common pattern rules as a prompt section (for prompt hardening) */
export function getFeaturePatternRules(): string {
  return [
    "## Block Generation Quality Rules",
    "",
    "1. **Interactive Bindings**: Every <button> and <link> MUST have a matching event binding.",
    '   - In events.json: {"buttonId:tap": {"flow": "flowName"}}',
    "   - The referenced flow MUST exist in flow.json with ≥1 step.",
    "",
    '2. **Cart Operations**: Cart blocks MUST use setState with target "appData.cart.items".',
    "   - Add: operation=append, Remove: operation=removeWhere",
    '   - UI MUST have iterateOver="#{appData.cart.items}" on a grid container.',
    "",
    "3. **Auth Flows**: Login/signup MUST include restApi step for server validation.",
    '   - POST to /api/auth/login or /api/auth/register',
    "   - Must setState for user profile after successful auth.",
    "",
    "4. **State Consistency**: Every setState target should be read by UI expressions.",
    "   - Don't write to state paths that nothing displays.",
    "   - Use consistent path conventions: appData.{domain}.{field}",
    "",
    "5. **Flow Completeness**: Every flow MUST have at least 1 step.",
    "   - Conditional flows need both then and else branches.",
    "   - API calls need resultTarget for downstream usage.",
  ].join("\n")
}
